import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ProductionCostCandidate, ProductionMaterialCostReport } from "../../../shared/production/material-cost";
import { apiRequest } from "../../shared/api/client";
import { filterLotCandidates, lotCostConfidence } from "./lot-cost-presentation";
export type LotCostOptions = { from: string; to: string; refreshVersion: number };
type CandidateResult = { source: "MICRO_ERP"; observedAt: string; truncated: boolean; items: ProductionCostCandidate[] };

export function useProductionLotCostWorkspace({ from, to, refreshVersion }: LotCostOptions) {
  const rangeKey = `${from}/${to}`;
  const [items, setItems] = useState<ProductionCostCandidate[]>([]);
  const [selected, setSelected] = useState<ProductionCostCandidate | null>(null);
  const [report, setReport] = useState<ProductionMaterialCostReport | null>(null);
  const [loadingList, setLoadingList] = useState(true), [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(""), [search, setSearch] = useState(""), [truncated, setTruncated] = useState(false);
  const version = useRef(0), mounted = useRef(true);
  const currentRange = useRef(rangeKey), stateRange = useRef(rangeKey);
  const itemsRef = useRef<ProductionCostCandidate[]>([]);
  const inFlight = useRef<string | null>(null);
  currentRange.current = rangeKey;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; version.current++; }; }, []);
  const loadDetail = useCallback(async (item: ProductionCostCandidate) => {
    const requestedRange = currentRange.current;
    if (!mounted.current || stateRange.current !== requestedRange || !itemsRef.current.some(candidate => candidate.id === item.id)) return;
    const key = `${requestedRange}/${item.id}`;
    if (inFlight.current === key) return;
    const current = ++version.current;
    inFlight.current = key;
    setSelected(item); setReport(null); setLoadingDetail(true); setError("");
    const isCurrent = () => mounted.current && current === version.current && requestedRange === currentRange.current;
    try {
      const params = new URLSearchParams({ company: String(item.company), branch: String(item.branch), series: item.series, serial: String(item.serial), line: String(item.line) });
      const result = await apiRequest<ProductionMaterialCostReport>(`/api/production/reports/material-cost?${params}`, { cache: "no-store" });
      if (isCurrent()) setReport(result);
    } catch (caught) {
      if (isCurrent()) setError(caught instanceof Error ? caught.message : "Maliyet hesabı tamamlanamadı.");
    } finally {
      if (isCurrent()) { inFlight.current = null; setLoadingDetail(false); }
    }
  }, []);
  const loadList = useCallback(async () => {
    if (!mounted.current || rangeKey !== currentRange.current) return;
    const current = ++version.current;
    stateRange.current = rangeKey; inFlight.current = null; itemsRef.current = [];
    setItems([]); setSelected(null); setReport(null); setLoadingDetail(false); setLoadingList(true); setError(""); setTruncated(false);
    const isCurrent = () => mounted.current && current === version.current && rangeKey === currentRange.current;
    try {
      const result = await apiRequest<CandidateResult>(`/api/production/reports/material-costs?${new URLSearchParams({ from, to })}`, { cache: "no-store" });
      if (!isCurrent()) return;
      itemsRef.current = result.items;
      setItems(result.items); setTruncated(result.truncated);
      const next = result.items[0] ?? null;
      setSelected(next); setLoadingList(false);
      if (next) void loadDetail(next);
    } catch (caught) {
      if (isCurrent()) { setLoadingList(false); setError(caught instanceof Error ? caught.message : "Maliyet üretimleri alınamadı."); }
    }
  }, [from, to, rangeKey, loadDetail]);
  useEffect(() => { void loadList(); return () => { version.current++; }; }, [loadList, refreshVersion]);
  const visible = stateRange.current === rangeKey;
  const visibleItems = visible ? items : [];
  const visibleReport = visible ? report : null;
  const filtered = useMemo(() => filterLotCandidates(visibleItems, search), [visibleItems, search]);
  return { items: visibleItems, selected: visible ? selected : null, report: visibleReport, loadingList: !visible || loadingList, loadingDetail: visible && loadingDetail, error: visible ? error : "", search, setSearch, truncated: visible && truncated, filtered, loadList, loadDetail, ...lotCostConfidence(visibleReport) };
}
